import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import {
  creditos,
  fecha,
  fechaHora,
  formatearCantidad,
  gramos,
} from "../lib/formato";
import { Cargando, Etiqueta, Panel, Vacio } from "../componentes/piezas";
import { useIdioma } from "../lib/idioma";
import { es, type Clave } from "../idiomas/es";

interface Historial {
  dispensaciones: {
    id: string;
    numero: number;
    fecha: string;
    totalMg: number;
    totalCentimos: number;
    descuentoCentimos: number;
    estado: string;
    metodoPago: string;
    lineas: {
      id: string;
      nombreProducto: string;
      cantidadMg: number;
      unidad: string;
      subtotalCentimos: number;
    }[];
  }[];
  cartera: {
    id: string;
    fecha: string;
    tipo: string;
    importeCentimos: number;
    saldoResultante: number;
    concepto: string | null;
    conceptoClave: string | null;
    conceptoDatos: string | null;
  }[];
  cuotas: {
    id: string;
    fecha: string;
    importeCentimos: number;
    periodoFin: string;
    concepto: string;
    conceptoClave: string | null;
    conceptoDatos: string | null;
  }[];
}

/** Todo lo del socio: sus retiradas, su cartera y sus cuotas. */
export default function PortalHistorial() {
  const { t, delConcepto } = useIdioma();
  const historial = useQuery({
    queryKey: ["portal-historial"],
    queryFn: () => api<Historial>("/portal/historial"),
  });

  if (historial.isLoading || !historial.data)
    return <Cargando texto={t("general.cargando")} />;
  const { dispensaciones, cartera, cuotas } = historial.data;

  // Un codigo que el diccionario no conoce se enseña tal cual, antes que en blanco.
  const etiqueta = (grupo: "pago" | "movimiento", codigo: string) => {
    const clave = `${grupo}.${codigo}` as Clave;
    return clave in es ? t(clave) : codigo;
  };

  return (
    <div className="columna" style={{ gap: 16 }}>
      <Panel titulo={t("historial.retiradas")} sinCuerpo>
        {dispensaciones.length === 0 ? (
          <Vacio>{t("historial.sinRetiradas")}</Vacio>
        ) : (
          <div className="tabla-envoltorio">
            <table className="tabla">
              <thead>
                <tr>
                  <th>{t("historial.fecha")}</th>
                  <th>{t("historial.productos")}</th>
                  <th className="num">{t("historial.cantidad")}</th>
                  <th className="num">{t("historial.aportacion")}</th>
                </tr>
              </thead>
              <tbody>
                {dispensaciones.map((d) => (
                  <tr
                    key={d.id}
                    style={
                      d.estado === "ANULADA" ? { opacity: 0.55 } : undefined
                    }
                  >
                    <td className="pequeno">
                      {fechaHora(d.fecha)}
                      {d.estado === "ANULADA" && (
                        <div>
                          <Etiqueta tipo="roja">
                            {t("historial.anulada")}
                          </Etiqueta>
                        </div>
                      )}
                    </td>
                    <td className="pequeno">
                      {d.lineas
                        .map(
                          (l) =>
                            `${l.nombreProducto} (${formatearCantidad(l.cantidadMg, l.unidad)})`,
                        )
                        .join(", ")}
                    </td>
                    <td className="num tabular">{gramos(d.totalMg)}</td>
                    <td className="num tabular">
                      {creditos(d.totalCentimos)}
                      <div className="suave pequeno">
                        {etiqueta("pago", d.metodoPago)}
                      </div>
                      {d.descuentoCentimos > 0 && (
                        <div
                          className="pequeno"
                          style={{ color: "var(--verde)" }}
                        >
                          −{creditos(d.descuentoCentimos)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel titulo={t("historial.cartera")} sinCuerpo>
        {cartera.length === 0 ? (
          <Vacio>{t("historial.sinMovimientos")}</Vacio>
        ) : (
          <div className="tabla-envoltorio">
            <table className="tabla">
              <thead>
                <tr>
                  <th>{t("historial.fecha")}</th>
                  <th>{t("historial.concepto")}</th>
                  <th className="num">{t("historial.importe")}</th>
                  <th className="num">{t("historial.saldo")}</th>
                </tr>
              </thead>
              <tbody>
                {cartera.map((m) => (
                  <tr key={m.id}>
                    <td className="pequeno">{fechaHora(m.fecha)}</td>
                    <td className="pequeno">
                      <Etiqueta>{etiqueta("movimiento", m.tipo)}</Etiqueta>{" "}
                      {delConcepto(m)}
                    </td>
                    <td
                      className="num tabular"
                      style={{
                        color:
                          m.importeCentimos >= 0
                            ? "var(--verde)"
                            : "var(--rojo)",
                      }}
                    >
                      {m.importeCentimos >= 0 ? "+" : ""}
                      {creditos(m.importeCentimos)}
                    </td>
                    <td className="num tabular">{creditos(m.saldoResultante)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel titulo={t("historial.cuotas")} sinCuerpo>
        {cuotas.length === 0 ? (
          <Vacio>{t("historial.sinCuotas")}</Vacio>
        ) : (
          <div className="tabla-envoltorio">
            <table className="tabla">
              <thead>
                <tr>
                  <th>{t("historial.fecha")}</th>
                  <th>{t("historial.concepto")}</th>
                  <th>{t("historial.validaHasta")}</th>
                  <th className="num">{t("historial.importe")}</th>
                </tr>
              </thead>
              <tbody>
                {cuotas.map((c) => (
                  <tr key={c.id}>
                    <td className="pequeno">{fecha(c.fecha)}</td>
                    <td className="pequeno">{delConcepto(c)}</td>
                    <td className="pequeno">{fecha(c.periodoFin)}</td>
                    <td className="num tabular">{creditos(c.importeCentimos)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
